import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { IdentifiableData, ISelectableData } from '@shared/models/generictablecolumn';
import { SelectManager } from './SelectManager';

interface ActionCounter<T extends IdentifiableData> {
	definition: SelectSubMenuItem<T>;
	enableCounter: number; // counts how many items are selected
	disableCounter: number; // counts how many items are selected to disable the action
}

export interface SelectSubMenuItem<T = any> extends Omit<SubMenuItem, 'click'> {
	click: (data: T[]) => void;
	filterMessage?: string;
	filter?: (rowData: T) => boolean;
	filterDisableMessage?: string;
	disableFilter?: (rowData: T) => boolean;
}

export class SelectSubMenuItemManager<T extends IdentifiableData> {
	constructor(actions?: SelectSubMenuItem<T>[]) {
		if (actions) this.setActions(actions, false);
	}

	selectManager?: SelectManager<T>;
	actions: Array<ActionCounter<T>> = [];

	setActions(actions: SelectSubMenuItem<T>[], resetSelection: boolean = true) {
		this.actions = actions.map((action) => ({
			enableCounter: 0,
			disableCounter: 0,
			definition: action,
		}));

		if (resetSelection) this.resetVisibleSelectActions();
	}

	setSelectManager(selectManager: SelectManager<T>) {
		this.selectManager = selectManager;

		selectManager.selectedEvent.subscribe((item: T) => {
			this.handleSelect(item, true);
		});

		selectManager.unSelectedEvent.subscribe((item: T) => {
			this.handleSelect(item, false);
		});

		selectManager.dataChanged.subscribe(() => {
			this.resetVisibleSelectActions();
		});
	}

	handleSelect(item: ISelectableData<any>, selected: boolean) {
		this.actions.forEach((action) => {
			if (action.definition.filter && action.definition.filter(item)) {
				if (selected) action.enableCounter++;
				else action.enableCounter--;
			}

			if (action.definition.disableFilter && action.definition.disableFilter(item)) {
				if (selected) action.disableCounter++;
				else action.disableCounter--;
			}
		});
	}

	getSubMenuItems(): SubMenuItem[] {
		return this.actions.map((action) => {
			return {
				click: () => {
					action.definition.click(this.selectManager?.getSelectedItems() || []);
				},
				disabled: () => {
					if (this.selectManager === undefined || this.selectManager.selectCount() === 0) {
						return true; // No items selected
					}

					if (action.definition.disableFilter && action.disableCounter > 0) {
						return true;
					}
					if (action.definition.filter && action.enableCounter === 0) {
						return true;
					}

					return false;
				},
				text: action.definition.text,
				icon: action.definition.icon,
				permissions: action.definition.permissions,
				disableOnMissingPermissions: action.definition.disableOnMissingPermissions,
				noText: action.definition.noText,
				tooltip: () => {
					if (this.selectManager === undefined || this.selectManager.selectCount() === 0) {
						return 'No items selected';
					}
					if (action.definition.disableFilter && action.disableCounter > 0) {
						return action.definition.filterMessage || 'Action is disabled due to filter';
					}
					if (action.definition.filter && action.enableCounter === 0) {
						return action.definition.filterDisableMessage || 'No items selected for this action';
					}
					return action.definition.tooltip?.() || '';
				},
				hideOnDisable: () => action.definition.hideOnDisable?.() ?? false,
			};
		});
	}

	resetVisibleSelectActions() {
		this.actions.forEach((action) => {
			action.enableCounter = 0;
			action.disableCounter = 0;
		});
	}
}
